/**
 * Универсальный http-клиент поверх axios.
 *
 * Что делает:
 *   • baseURL: `${VITE_API_BASE_URL}/v1/office` (флаги withoutVersion/
 *     withoutOffice позволяют выйти за этот префикс — например для
 *     `/storage/...`).
 *   • Bearer-токен из localStorage (`TOKEN_STORAGE_KEY`). Тот же ключ
 *     пишет `atomWithStorage` в `atoms/auth/auth.atoms.ts` — поэтому
 *     обе стороны автоматически синхронизированы.
 *   • Country-Code header из env.
 *   • snake_case ↔ camelCase конверсия тел и query-параметров.
 *   • Распаковка legacy-конверта `{ result, status, payload }` — при
 *     `result !== 0` кидаем `HttpError`, в success возвращаем `payload`.
 *   • 401 → throw `HttpError`. Обработку (logout + navigate) делает
 *     слой выше (QueryClient global onError, добавится в шаге 4c).
 */

import axios, {type AxiosError, type AxiosInstance, type AxiosRequestConfig} from 'axios';

import {TOKEN_STORAGE_KEY} from './auth-storage';
import {toCamel, toSnake} from './case-convert';
import {env} from './env';
import {HttpError, type HttpOptions} from './http.types';

/* ── Axios instance ───────────────────────────────────────────── */

/** Строим базовый URL. Относительный (/api) остаётся относительным. */
const baseUrl = (withoutVersion: boolean, withoutOffice: boolean): string => {
  const base = env.VITE_API_BASE_URL.replace(/\/$/, '');
  const version = withoutVersion ? '' : '/v1';
  const scope = withoutOffice ? '' : '/office';
  return base + version + scope;
};

const client: AxiosInstance = axios.create({
  baseURL: baseUrl(false, false),
  withCredentials: false,
  headers: {Accept: '*/*'},
});

/* ── Interceptors ─────────────────────────────────────────────── */

/**
 * Читаем токен из localStorage. `atomWithStorage` пишет через
 * `JSON.stringify`, поэтому строка хранится в кавычках — нужно
 * парсить обратно перед использованием в Bearer-заголовке. Иначе
 * BE получает `Bearer "abc"` и кидает `Invalid crypto padding`.
 */
const readToken = (): string | null => {
  const raw = localStorage.getItem(TOKEN_STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    return typeof parsed === 'string' ? parsed : null;
  } catch {
    // Старая запись без JSON-обёртки — используем как есть.
    return raw;
  }
};

client.interceptors.request.use((config) => {
  config.headers.set('Country-Code', env.VITE_COUNTRY_CODE);

  // Токен — напрямую из localStorage. atomWithStorage пишет туда же,
  // так что atoms/auth и http смотрят на один источник.
  const token = readToken();
  if (token) config.headers.set('Authorization', `Bearer ${token}`);

  // Snake-case body+params (если явно не отключено через skipCaseConversion).
  const skip = (config as {skipCaseConversion?: boolean}).skipCaseConversion === true;
  if (!skip) {
    if (config.data && !(config.data instanceof FormData)) {
      config.data = toSnake(config.data);
    }
    if (config.params && typeof config.params === 'object') {
      config.params = toSnake(config.params);
    }
  }

  // Переопределение baseURL для withoutVersion / withoutOffice.
  const cfgX = config as AxiosRequestConfig & {
    withoutVersion?: boolean;
    withoutOffice?: boolean;
  };
  if (cfgX.withoutVersion || cfgX.withoutOffice) {
    config.baseURL = baseUrl(!!cfgX.withoutVersion, !!cfgX.withoutOffice);
  }

  return config;
});

client.interceptors.response.use(
  (res) => {
    const skip = (res.config as {skipCaseConversion?: boolean}).skipCaseConversion === true;
    res.data = skip ? res.data : toCamel(res.data);
    return res;
  },
  (err: AxiosError) => {
    const status = err.response?.status ?? 0;
    const body = err.response?.data;
    return Promise.reject(
      new HttpError(err.response?.statusText || err.message || 'HTTP error', status, body),
    );
  },
);

/* ── Public API ───────────────────────────────────────────────── */

/**
 * Основной fetch-хелпер. Все сервисы зовут только его.
 *
 *   const user = await http<User>('me');
 *   await http('order/item', {method: 'POST', body: {…}});
 */
export const http = async <T = unknown>(path: string, options: HttpOptions = {}): Promise<T> => {
  const {body, params, withoutVersion, withoutOffice, skipCaseConversion, rawResponse, ...rest} =
    options;

  // Чистим undefined/null params, чтобы axios их не сериализовал.
  const cleanParams = params
    ? Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null))
    : undefined;

  const res = await client.request<unknown>({
    url: path.startsWith('/') ? path.slice(1) : path,
    method: rest.method ?? (body !== undefined ? 'POST' : 'GET'),
    data: body,
    params: cleanParams,
    ...rest,
    ...(withoutVersion ? {withoutVersion: true} : {}),
    ...(withoutOffice ? {withoutOffice: true} : {}),
    ...(skipCaseConversion ? {skipCaseConversion: true} : {}),
  } as AxiosRequestConfig);

  const data = res.data;

  if (rawResponse) return data as T;

  // Legacy envelope: { result, status, payload }.
  // result !== 0 → ошибка (HTTP остаётся 200), нужна явная распаковка.
  if (data && typeof data === 'object') {
    const envelope = data as {
      result?: number;
      status?: string;
      payload?: unknown;
    };
    if (envelope.result !== undefined && envelope.result !== 0) {
      throw new HttpError(envelope.status ?? 'api_error', res.status, envelope);
    }
    if ('payload' in envelope) return envelope.payload as T;
  }

  return data as T;
};
